import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { BRAND } from "../config/brand";

export const brandText = (value) => String(value || "").replaceAll("일광오피스", BRAND.name);
const base = BRAND.url.replace(/\/$/, "");
const normalizePath = (value) => {
  try { return decodeURIComponent(value || "/").replace(/\/$/, "") || "/"; }
  catch { return value || "/"; }
};
const absolute = (value) => {
  try {
    const url = new URL(value || "/og-image.png", `${base}/`);
    return /^https?:$/.test(url.protocol) ? url.href : `${base}/og-image.png`;
  } catch { return `${base}/og-image.png`; }
};
const json = (value) => JSON.stringify(value).replace(/</g, "\\u003c");

export default function SEO({ title, description, keywords, path = "/", noindex = false, product = null, breadcrumbs = null, areaServed = "", children }) {
  const location = useLocation();
  const initial = window.__PAGE_SEO__;
  const currentPath = normalizePath(path);
  const server = initial && normalizePath(initial.path) === currentPath && normalizePath(location.pathname) === currentPath ? initial : null;
  let pageTitle = brandText(server?.title || (currentPath === "/" && BRAND.seoTitle) || title || `복합기·복사기 렌탈 상담 | ${BRAND.name}`);
  if (!pageTitle.includes(BRAND.name)) pageTitle += ` | ${BRAND.name}`;
  const pageDesc = brandText(server?.description || (currentPath === "/" && BRAND.seoDescription) || description || `${BRAND.name}에서 복합기·프린터의 사양과 렌탈·구매 조건을 비교하세요. 월 출력량과 설치 환경에 맞춰 상담합니다.`);
  const canonical = absolute(currentPath);
  const image = absolute(product?.image);
  const excluded = noindex || server?.noindex;
  const offers = [];
  if (product) {
    [["price", "구매", ["sale", "both"]], ["rentalPrice", "월 렌탈", ["rental", "both"]]].forEach(([key, name, types]) => {
      const price = Number(product[key]);
      if (!(price > 0) || !types.includes(product.saleType)) return;
      offers.push({ "@type": "Offer", name, url: canonical, price, priceCurrency: "KRW",
        seller: { "@type": "Organization", name: BRAND.name, url: base },
        ...(key === "rentalPrice" ? { businessFunction: "http://purl.org/goodrelations/v1#LeaseOut", priceSpecification: { "@type": "UnitPriceSpecification", price, priceCurrency: "KRW", unitText: "월" } } : {}),
      });
    });
  }
  const schemas = [];
  if (product) schemas.push({ "@context": "https://schema.org", "@type": "Product", name: product.name, url: canonical, description: pageDesc,
    ...(product.image ? { image } : {}), ...(product.brand ? { brand: { "@type": "Brand", name: product.brand } } : {}),
    sku: product.sku || `IK-${product.id}`, ...(offers.length ? { offers } : {}),
  });
  if (breadcrumbs) schemas.push({ "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: breadcrumbs.map((b, i) => ({ "@type": "ListItem", position: i + 1, name: b.name, item: b.url ? absolute(b.url) : canonical })) });
  if (areaServed) schemas.push({ "@context": "https://schema.org", "@type": "Service", serviceType: "복합기·복사기 렌탈", name: pageTitle, description: pageDesc, url: canonical,
    areaServed: { "@type": "AdministrativeArea", name: areaServed }, provider: { "@type": "Organization", name: BRAND.name, telephone: BRAND.phone, url: base },
  });
  if (currentPath === "/") schemas.push(
    { "@context": "https://schema.org", "@type": "Organization", name: BRAND.name, legalName: "일광엔시스", url: base, logo: `${base}/logo512.png`, telephone: BRAND.phone },
    { "@context": "https://schema.org", "@type": "WebSite", name: BRAND.name, url: base },
  );
  return <Helmet>
    <title>{pageTitle}</title>
    <meta name="description" content={pageDesc} />
    {keywords && <meta name="keywords" content={brandText(keywords)} />}
    <link rel="canonical" href={canonical} />
    <meta name="robots" content={excluded ? "noindex, follow" : "index, follow, max-image-preview:large"} />
    <meta property="og:title" content={pageTitle} />
    <meta property="og:description" content={pageDesc} />
    <meta property="og:url" content={canonical} />
    <meta property="og:site_name" content={BRAND.name} />
    <meta property="og:locale" content="ko_KR" />
    <meta property="og:type" content={product ? "product" : "website"} />
    <meta property="og:image" content={image} />
    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:title" content={pageTitle} />
    <meta name="twitter:description" content={pageDesc} />
    <meta name="twitter:image" content={image} />
    {schemas.map((schema, i) => <script key={i} type="application/ld+json">{json(schema)}</script>)}
    {children}
  </Helmet>;
}
